import { useState } from "react";
import useEvaApi, { EvaApiCall } from "../../hooks/useEvaApi";
import { getStep } from "./steps/Steps";
import { useAuthContext } from "../../context/AuthContext";
import { getStepFetchParams } from "./FlowPlayerHelpers";
import Loader from "../loader/Loader";

export class FlowPlayer {
  constructor() {
    this.flowId = null;
    this.simulated = false;
    this.submitted = false;
    this.visitor = null;
    this.visitorData = {};
    this.setVisitorData = () => {};
    this.visitorType = null;
    this.onSubmit = () => {};
    this.onFlowEnd = () => {};
    this.onPreviousStepNotFound = () => {};
    this.onStepChanged = () => {};
    this.onBeforeNextStep = () => true;
    this.onCancelPress = null;
  }

  setFlowId(flowId) {
    this.flowId = flowId;
    return this;
  }

  setSimulated(simulated) {
    this.simulated = simulated;
    return this;
  }

  setSubmitted(submitted) {
    this.submitted = submitted;
    return this;
  }

  setOnSubmit(onSubmit) {
    this.onSubmit = onSubmit;
    return this;
  }

  setOnFlowEnd(onFlowEnd) {
    this.onFlowEnd = onFlowEnd;
    return this;
  }

  setOnPreviousStepNotFound(onPreviousStepNotFound) {
    this.onPreviousStepNotFound = onPreviousStepNotFound;
    return this;
  }

  setOnStepChanged(onStepChanged) {
    this.onStepChanged = onStepChanged;
    return this;
  }

  /**
   * @param {function(object): boolean} onBeforeNextStep called with the current step before going to the next step.
   * Return false to stay on the current step, for example when the flow should not be continued.
   * @returns {FlowPlayer}
   */
  setOnBeforeNextStep(onBeforeNextStep) {
    this.onBeforeNextStep = onBeforeNextStep;
    return this;
  }

  setVisitorState(visitorData, setVisitorData) {
    this.visitorData = visitorData;
    this.setVisitorData = setVisitorData;
    return this;
  }

  setVisitor(visitor) {
    this.visitor = visitor;
    return this;
  }

  setVisitorType(visitorType) {
    this.visitorType = visitorType;
    return this;
  }

  setOnCancelPress(onCancelPress) {
    this.onCancelPress = onCancelPress;
    return this;
  }
}

export default function useFlowPlayer(controller) {
  const callEvaApi = useEvaApi();
  const authContext = useAuthContext();
  const [step, setStep] = useState(null);
  const [firstStepId, setFirstStepId] = useState(null);
  const [errors, setErrors] = useState({});
  const [fetchedEntities, setFetchedEntities] = useState({
    fields: [],
    employees: [],
  });
  const [stepResults, setStepResults] = useState({});
  const flowPlayer = {
    //Add all the controller properties from the FlowPlayer class, ignore methods
    ...controller,
    onNextStep,
    onPreviousStep,
    step,
    firstStepId,
    renderStep,
    errors,
    fetchedEntities,
    setFetchedEntities,
    stepResults,
    getFlowType,
  };

  /**
   * This method will try to get the next step
   */
  async function onNextStep(currentStep = null) {
    currentStep = currentStep ?? step;

    //Let the controller stop the flow before leaving the current step
    if (currentStep && controller.onBeforeNextStep(currentStep) === false) {
      return;
    }

    if (!controller.flowId) {
      controller.onSubmit(currentStep);
      return;
    }

    await callEvaApi(
      new EvaApiCall(
        `flows/${controller.flowId}/steps/next${currentStep ? `/${currentStep?.id}` : ""}`,
      )
        .setLoadingGroup(
          currentStep ? "flow_player_next_step" : "flow_player_fetch",
        )
        .setMethod("POST")
        .setParams(getStepFetchParams())
        .setErrorState(setErrors)
        .setData({ ...controller.visitorData, step_results: stepResults })
        .setAlertError(false)
        .setAlertSuccess(false)
        .setOnSuccess((response) => {
          // If there is no new step, go back to the start page
          const newStep = response.data.data;
          if (!newStep) {
            controller.onFlowEnd();
            return;
          }

          //Store the results of the step, so they can be used later in the flow
          if (newStep.step_results) {
            setStepResults((prev) => ({ ...prev, ...newStep.step_results }));
          }

          //Copy the validated references to the configured field of the current step
          applyReferenceField(currentStep);

          //Update the step
          updateStep(newStep);

          //If we reached the process data step, submit the form
          if (newStep.type === "process_data") {
            controller.onSubmit(newStep);
            return;
          }

          // If the flow is simulated, fetch the next step
          if (controller.simulated && !newStep.force_show) {
            onNextStep(newStep);
          }
        }),
    );
  }

  /**
   * This method will try to go back to the previous step
   * @param {int|null} stepId
   */
  async function onPreviousStep() {
    await callEvaApi(
      new EvaApiCall(`flows/${controller.flowId}/steps/previous/${step?.id}`)
        .setLoadingGroup("flow_player_previous_step")
        .setMethod("POST")
        .setParams(getStepFetchParams())
        .setAlertSuccess(false)
        .setOnSuccess((response) => {
          const newStep = response.data.data;
          if (!newStep) {
            controller.onPreviousStepNotFound();
            return;
          }

          updateStep(newStep);
        }),
    );
  }

  /**
   * This method will update the step and call the onStepChanged callback
   * @param {object} step
   */
  function updateStep(step) {
    if (!firstStepId) setFirstStepId(step?.id);
    setStep(step);
    controller.onStepChanged(step);
  }

  /**
   * This method will copy the validated references to the field configured on the given step
   * @param {object} currentStep
   */
  function applyReferenceField(currentStep) {
    const referenceFieldId = currentStep?.reference_field?.id;
    if (!referenceFieldId) return;

    const references = controller.visitorData.reference ?? [];
    if (references.length === 0) return;

    controller.setVisitorData((prev) => ({
      ...prev,
      [`field_${referenceFieldId}`]: references.filter(Boolean).join(", "),
    }));
  }

  /**
   * This method will return the flow type based on the user role
   * @returns {string}
   */
  function getFlowType() {
    switch (authContext.auth?.user?.role_type) {
      case "terminal":
        return "terminal";
      case "visitor":
        return "portal";
      default:
        return "backoffice";
    }
  }

  /**
   * This method will render the step component
   * @returns {JSX.Element|null}
   */
  function renderStep() {
    const StepComponent = getStep(step?.type);
    // Key on the step id, so consecutive steps of the same type don't share state
    return StepComponent ? (
      <StepComponent key={step?.id} controller={flowPlayer} />
    ) : (
      <div
        style={{
          padding: 20,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader groups="flow_player_fetch" iconSize="lg" />
      </div>
    );
  }

  return flowPlayer;
}
